import { ref } from 'vue'

// Global state for snackbars
const snackbars = ref([])
let snackbarIdCounter = 0

export const useSnackbarManager = () => {
  const addSnackbar = (spec) => {
    // Only one snackbar at a time — remove any existing ones first
    snackbars.value = []

    const id = ++snackbarIdCounter
    const snackbar = {
      id,
      show: true,
      timeout: 3500,
      color: 'success',
      ...spec
    }
    snackbars.value.push(snackbar)
    
    // Auto-remove after timeout
    if (snackbar.timeout > 0) {
      setTimeout(() => removeSnackbar(id), snackbar.timeout)
    }
    
    return id
  }

  const removeSnackbar = (id) => {
    const index = snackbars.value.findIndex(s => s.id === id)
    if (index > -1) {
      snackbars.value.splice(index, 1)
    }
  }

  const closeSnackbar = (id) => {
    const snackbar = snackbars.value.find(s => s.id === id)
    if (snackbar) {
      snackbar.show = false
      // Remove after animation
      setTimeout(() => removeSnackbar(id), 300)
    }
  }

  const closeAll = () => {
    snackbars.value.forEach(snackbar => {
      snackbar.show = false
    })
    // Clear all after animation
    setTimeout(() => {
      snackbars.value = []
    }, 300)
  }

  return {
    snackbars,
    addSnackbar,
    removeSnackbar,
    closeSnackbar,
    closeAll
  }
}

// Simple API for easy usage
export const SimpleSnackbar = {
  show(spec = {}) {
    const { addSnackbar } = useSnackbarManager()
    return addSnackbar(spec)
  },

  success(message, options = {}) {
    return this.show({
      message,
      color: 'success',
      ...options
    })
  },

  error(message, options = {}) {
    return this.show({
      message,
      color: 'error',
      timeout: -1, // No timeout for errors - user must dismiss
      ...options
    })
  },

  info(message, options = {}) {
    return this.show({
      message,
      color: 'info',
      ...options
    })
  },

  warning(message, options = {}) {
    return this.show({
      message,
      color: 'warning',
      ...options
    })
  },

  // Standard CRUD messages
  created(entityName = 'item', options = {}) {
    return this.success(`${entityName} added`, options)
  },

  saved(entityName = 'item', options = {}) {
    return this.success(`${entityName} updated`, options)
  },

  deleted(entityName = 'item', options = {}) {
    return this.success(`${entityName} deleted`, options)
  },

  // Standard error handler
  apiError(error, options = {}) {
    // Maintenance popup already showed the message — don't stack a second one
    if (error?.isMaintenance) return

    let message

    if (error?.isTimeoutError) {
      // Timeout error - server took too long to respond
      message = 'Request timed out. The server took too long to respond.'
    } else if (error?.isNetworkError) {
      // Network error - request never reached the server
      // Include debug info to help troubleshooting
      const debugParts = []
      if (error.requestMethod && error.requestUrl) {
        debugParts.push(`${error.requestMethod} ${error.requestUrl}`)
      }
      if (error.errorCode) {
        debugParts.push(error.errorCode)
      }
      const debugInfo = debugParts.length > 0 ? ` (${debugParts.join(' - ')})` : ''
      message = `Network error. Please check your connection.${debugInfo}`
    } else if (error?.statusCode === 403) {
      message = 'You do not have permission to perform this action.'
    } else if (error?.statusCode >= 500) {
      // Server errors (5xx)
      message = 'Server error. Please try again later.'
    } else if (error?.statusCode === 404) {
      message = 'The requested resource was not found.'
    } else if (error?.statusCode === 400) {
      // Bad request - use the error message from server
      message = error?.message || 'Invalid request.'
    } else if (error?.statusCode === 401) {
      // Check if user is authenticated - if so, it's likely a permission/authorization issue
      // rather than authentication issue
      const authStore = this._getAuthStore()
      if (authStore && authStore.isAuthenticated) {
        message = 'You do not have permission to perform this action.'
      } else {
        message = 'You are not authenticated. Please log in.'
      }
    } else {
      // Default fallback
      message = error?.message || 'An error occurred'
    }

    // Append correlation ID for debugging (helps correlate with backend logs)
    if (error?.correlationId) {
      message += ` (Request ID: ${error.correlationId})`
    }

    return this.error(message, options)
  },

  // Helper to get auth store (lazy loaded to avoid circular dependencies)
  _getAuthStore() {
    try {
      // Dynamic import to avoid circular dependency issues
      const { useAuthStore } = require('../stores/authStore')
      return useAuthStore()
    } catch (e) {
      console.warn('Could not load auth store:', e)
      return null
    }
  }
}
